import { useState, useRef } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import Logo from "@/components/Logo";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import {
  Clock, BarChart3, CalendarCheck, Monitor, Palette, IdCard, FileText, Building2,
  Check, ArrowRight, Menu, X, ShieldCheck, Star, Loader2, MapPin, QrCode, Sparkles, Mail,
  CalendarDays, Home, Wallet, FolderArchive,
} from "lucide-react";

const CONTACT_EMAIL = "hola@fichando.app";

const HERO_IMG = "https://images.unsplash.com/photo-1572021335469-31706a17aaef?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NTZ8MHwxfHNlYXJjaHwyfHxkaXZlcnNlJTIwYnVzaW5lc3MlMjB0ZWFtJTIwb2ZmaWNlfGVufDB8fHx8MTc4MTI3NDUzMHww&ixlib=rb-4.1.0&q=85";
const KIOSK_IMG = "https://images.pexels.com/photos/8961008/pexels-photo-8961008.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";
const T1 = "https://images.unsplash.com/photo-1508002366005-75a695ee2d17?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxODd8MHwxfHNlYXJjaHwyfHxoYXBweSUyMGVtcGxveWVlJTIwcG9ydHJhaXR8ZW58MHx8fHwxNzgxMjc0NTMwfDA&ixlib=rb-4.1.0&q=85";
const T2 = "https://images.pexels.com/photos/12902933/pexels-photo-12902933.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";

const FEATURES = [
  { icon: Clock, title: "Fichaje en segundos", desc: "Entrada, salida y pausas desde el móvil, el ordenador o el quiosco. Sin fricción para tu equipo.", span: "md:col-span-2" },
  { icon: BarChart3, title: "Dashboard en tiempo real", desc: "Horas, ausencias y horas extra por departamento y empleado. Filtra por día, mes o año.", span: "" },
  { icon: CalendarDays, title: "Turnos y horarios", desc: "Define jornadas, turnos y horarios flexibles por empleado o departamento. Días libres y festivos incluidos.", span: "" },
  { icon: Home, title: "Teletrabajo", desc: "Tus empleados fichan indicando si trabajan en oficina o en remoto. Visibilidad total en informes.", span: "" },
  { icon: CalendarCheck, title: "Calendario de equipo", desc: "Vista mensual con fichajes, teletrabajo, vacaciones, ausencias y festivos de un vistazo.", span: "md:col-span-2" },
  { icon: Monitor, title: "Modo Quiosco", desc: "Una tablet en la entrada. Fichaje ultrarrápido por PIN o código QR.", span: "" },
  { icon: CalendarCheck, title: "Vacaciones y ausencias", desc: "Solicitudes y aprobaciones con un clic. Tu equipo siempre informado.", span: "md:col-span-2" },
  { icon: Wallet, title: "Gastos con IA", desc: "Tus empleados fotografían el ticket y la IA rellena importe, IVA y categoría. Repercute gastos a cada trabajo y exporta a contabilidad.", span: "md:col-span-2" },
  { icon: FolderArchive, title: "Gestión documental", desc: "Contratos, nóminas, formación, PRL y EPIs en un solo sitio. Acceso para tu gestoría, avisos de caducidad y una zona de salud privada protegida por PIN.", span: "md:col-span-2" },
  { icon: Palette, title: "Marca blanca", desc: "Tu logo y tus colores. Fichando se adapta a tu empresa.", span: "" },
  { icon: IdCard, title: "Carnet con QR", desc: "Identificación de empleados con foto y QR verificable.", span: "" },
  { icon: FileText, title: "Informes legales", desc: "Exporta el registro de jornada en PDF/CSV en un clic. Cumplimiento garantizado.", span: "" },
];

const PLANS = [
  { name: "Básico", monthly: 29, desc: "Para equipos pequeños que empiezan.", popular: false, launchYear1: 99, features: ["Hasta 15 empleados", "Fichaje web + quiosco", "Vacaciones y ausencias", "Documentos de RRHH", "Informes CSV", "Soporte por email"] },
  { name: "Pro", monthly: 49, desc: "El favorito de las pymes en crecimiento.", popular: true, launchPct: 0.5, features: ["Hasta 50 empleados", "Todo lo de Básico", "Turnos y horarios flexibles", "Teletrabajo y calendario de equipo", "Gestión de gastos con IA", "Documentos: Formación y PRL/EPIs", "Dashboard analítico", "Marca blanca completa", "Carnet con QR", "Soporte prioritario"] },
  { name: "Empresa", monthly: null, desc: "Multi-centro y gestión documental avanzada.", popular: false, note: "Incluye gestión documental completa, zona de salud y usuario para tu gestoría.", features: ["Empleados ilimitados", "Multi-centro / multi-sede", "Todo lo de Pro", "Zona de salud privada (PIN del empleado)", "Usuario Gestoría con permisos", "Avisos de caducidad de documentos", "Dominio o subdominio propio (tuempresa.fichando.app)", "SLA y soporte dedicado"] },
];

const fmtEur = (n) => `${n.toFixed(2).replace(".", ",")}€`;

const STEPS = [
  { n: "01", title: "Crea tu empresa", desc: "Configura tu marca, departamentos y da de alta a tu equipo en minutos." },
  { n: "02", title: "Tu equipo ficha", desc: "Desde el móvil, el ordenador o la tablet en modo quiosco con PIN/QR." },
  { n: "03", title: "Controla y decide", desc: "Analiza horas, aprueba ausencias y exporta informes legales al instante." },
];

const fadeUp = { hidden: { opacity: 0, y: 24 }, show: (i = 0) => ({ opacity: 1, y: 0, transition: { duration: 0.5, delay: i * 0.08 } }) };

function NavBar() {
  const [open, setOpen] = useState(false);
  const links = [["Características", "#features"], ["Precios", "#pricing"], ["Contacto", "#contact"]];
  return (
    <header className="fixed top-0 w-full z-50 bg-white/70 backdrop-blur-xl border-b border-slate-200/50">
      <div className="max-w-7xl mx-auto px-6 lg:px-8 h-16 flex items-center justify-between">
        <a href="#top" className="flex items-center gap-2" data-testid="nav-logo">
          <Logo size={38} textSize="text-xl" />
        </a>
        <nav className="hidden md:flex items-center gap-8">
          {links.map(([l, h]) => <a key={h} href={h} className="text-sm font-medium text-slate-600 hover:text-slate-900 transition-colors">{l}</a>)}
        </nav>
        <div className="hidden md:flex items-center gap-3">
          <Link to="/login" className="text-sm font-semibold text-slate-700 hover:text-slate-900" data-testid="nav-login">Iniciar sesión</Link>
          <Link to="/login" data-testid="nav-demo-btn">
            <Button className="rounded-full bg-[#003366] hover:bg-[#04284a] text-white shadow-lg shadow-[#003366]/25 hover:-translate-y-0.5 transition-all">Probar demo</Button>
          </Link>
        </div>
        <button className="md:hidden" onClick={() => setOpen(!open)} data-testid="nav-mobile-toggle">{open ? <X /> : <Menu />}</button>
      </div>
      {open && (
        <div className="md:hidden bg-white border-b border-slate-200 px-6 py-4 space-y-3">
          {links.map(([l, h]) => <a key={h} href={h} onClick={() => setOpen(false)} className="block text-slate-700 font-medium">{l}</a>)}
          <Link to="/login" className="block"><Button className="w-full rounded-full bg-[#003366] text-white">Probar demo</Button></Link>
        </div>
      )}
    </header>
  );
}

function ContactForm() {
  const [form, setForm] = useState({ name: "", email: "", company: "", phone: "", employees: "", message: "", plan: "", website: "" });
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email) return toast.error("Indica al menos tu nombre y email");
    setLoading(true);
    try {
      await api.post("/contact", form);
      setSent(true);
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setLoading(false); }
  };

  if (sent) {
    return (
      <div className="bg-white rounded-3xl border border-slate-200/60 shadow-sm p-10 text-center" data-testid="contact-success">
        <div className="mx-auto h-16 w-16 rounded-full bg-emerald-100 flex items-center justify-center mb-4">
          <Check className="h-8 w-8 text-emerald-600" />
        </div>
        <h3 className="font-heading text-2xl font-bold tracking-tight">¡Gracias por tu interés!</h3>
        <p className="mt-3 text-slate-600 leading-relaxed">Hemos recibido tu solicitud. Nuestro equipo te contactará en menos de 24 horas para preparar tu demo personalizada.</p>
        <div className="mt-7 flex flex-col sm:flex-row gap-3 justify-center">
          <Link to="/login"><Button className="rounded-full bg-[#003366] hover:bg-[#04284a] text-white font-semibold px-7">Probar la demo ahora</Button></Link>
          <Button variant="outline" className="rounded-full border-slate-200 font-semibold px-7" onClick={() => { setSent(false); setForm({ name: "", email: "", company: "", phone: "", employees: "", message: "", plan: "", website: "" }); }} data-testid="contact-reset">Enviar otra consulta</Button>
        </div>
      </div>
    );
  }

  return (
    <form onSubmit={submit} className="bg-white rounded-3xl border border-slate-200/60 shadow-sm p-8 md:p-10 space-y-4" data-testid="contact-form">
      {/* Honeypot anti-spam: oculto para humanos */}
      <input type="text" name="website" tabIndex={-1} autoComplete="off" value={form.website} onChange={(e) => setForm({ ...form, website: e.target.value })} className="hidden" aria-hidden="true" />
      <div className="grid sm:grid-cols-2 gap-4">
        <div><Label>Nombre *</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1.5 h-11 rounded-xl bg-slate-50 border-transparent focus:bg-white focus:border-[#003366]" data-testid="contact-name" /></div>
        <div><Label>Email *</Label><Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="mt-1.5 h-11 rounded-xl bg-slate-50 border-transparent focus:bg-white focus:border-[#003366]" data-testid="contact-email" /></div>
      </div>
      <div className="grid sm:grid-cols-2 gap-4">
        <div><Label>Empresa</Label><Input value={form.company} onChange={(e) => setForm({ ...form, company: e.target.value })} className="mt-1.5 h-11 rounded-xl bg-slate-50 border-transparent focus:bg-white focus:border-[#003366]" data-testid="contact-company" /></div>
        <div><Label>Teléfono</Label><Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="mt-1.5 h-11 rounded-xl bg-slate-50 border-transparent focus:bg-white focus:border-[#003366]" data-testid="contact-phone" /></div>
      </div>
      <div><Label>Nº de empleados</Label><Input value={form.employees} onChange={(e) => setForm({ ...form, employees: e.target.value })} placeholder="ej. 25" className="mt-1.5 h-11 rounded-xl bg-slate-50 border-transparent focus:bg-white focus:border-[#003366]" data-testid="contact-employees" /></div>
      <div><Label>¿En qué podemos ayudarte?</Label><Textarea value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} rows={4} className="mt-1.5 rounded-xl bg-slate-50 border-transparent focus:bg-white focus:border-[#003366]" data-testid="contact-message" /></div>
      <Button type="submit" disabled={loading} data-testid="contact-submit" className="w-full h-12 rounded-full accent-btn font-semibold shadow-lg shadow-[#FF8C00]/30 hover:-translate-y-0.5">
        {loading ? <Loader2 className="h-5 w-5 animate-spin" /> : <>Solicitar demo gratuita <ArrowRight className="h-4 w-4 ml-2" /></>}
      </Button>
      <p className="text-xs text-slate-400 text-center">Sin compromiso. Te respondemos en menos de 24h.</p>
    </form>
  );
}

export default function Landing() {
  const contactRef = useRef(null);
  const [annual, setAnnual] = useState(false);
  const scrollContact = (plan = "") => {
    document.getElementById("contact")?.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <div className="min-h-screen bg-slate-50 text-slate-900" id="top" style={{ fontFamily: "'Manrope', sans-serif" }}>
      <NavBar />

      {/* HERO */}
      <section className="relative pt-32 pb-20 md:pt-44 md:pb-28 overflow-hidden">
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[800px] h-[500px] bg-[#9bbfe0]/40 blur-[120px] rounded-full -z-0" />
        <div className="max-w-7xl mx-auto px-6 lg:px-8 relative z-10 text-center">
          <motion.div initial="hidden" animate="show" variants={fadeUp} className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-white border border-slate-200 text-sm font-medium text-slate-600 mb-6">
            <Sparkles className="h-4 w-4 text-[#003366]" /> Control horario que tu equipo querrá usar
          </motion.div>
          <motion.h1 initial="hidden" animate="show" custom={1} variants={fadeUp} className="font-heading text-5xl md:text-6xl lg:text-7xl font-extrabold tracking-tighter leading-[0.95]">
            El control horario,<br /><span className="text-[#003366]">por fin sencillo.</span>
          </motion.h1>
          <motion.p initial="hidden" animate="show" custom={2} variants={fadeUp} className="mt-6 text-lg md:text-xl text-slate-600 max-w-2xl mx-auto leading-relaxed">
            Fichaje, vacaciones, informes legales y modo quiosco para tablets. Todo en una plataforma
            marca blanca pensada para empresarios que quieren dedicar su tiempo a lo importante.
          </motion.p>
          <motion.div initial="hidden" animate="show" custom={3} variants={fadeUp} className="mt-9 flex flex-col sm:flex-row gap-3 justify-center">
            <Link to="/login" data-testid="hero-demo-button">
              <Button className="rounded-full accent-btn shadow-lg shadow-[#FF8C00]/30 hover:-translate-y-0.5 px-8 py-6 text-base font-semibold">Probar demo gratis</Button>
            </Link>
            <Button onClick={() => scrollContact()} data-testid="hero-contact-button" variant="outline" className="rounded-full bg-white border-slate-200 hover:bg-slate-50 hover:-translate-y-0.5 transition-all px-8 py-6 text-base font-semibold shadow-sm">Solicitar demo</Button>
          </motion.div>
          <motion.div initial={{ opacity: 0, y: 40 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.4 }} className="mt-16 max-w-5xl mx-auto">
            <div className="rounded-3xl overflow-hidden shadow-2xl shadow-slate-300/50 border border-slate-200/60">
              <img src={HERO_IMG} alt="Equipo de empresa usando Fichando" className="w-full h-auto" />
            </div>
          </motion.div>
        </div>
      </section>

      {/* TRUST */}
      <section className="py-10 border-y border-slate-200/60 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <p className="text-center text-xs font-bold tracking-[0.2em] uppercase text-slate-400 mb-6">Empresas que confían en el control horario sin complicaciones</p>
          <div className="flex flex-wrap justify-center items-center gap-x-12 gap-y-4 text-slate-400">
            {["Construcción", "Logística", "Oficinas", "Hostelería", "Retail", "Industria"].map((s) => (
              <span key={s} className="font-heading font-bold text-lg md:text-xl text-slate-300">{s}</span>
            ))}
          </div>
        </div>
      </section>

      {/* FEATURES */}
      <section id="features" className="py-24 md:py-32">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="max-w-2xl mb-14">
            <p className="text-xs font-bold tracking-[0.2em] uppercase text-[#003366] mb-3">Características</p>
            <h2 className="font-heading text-3xl md:text-4xl font-bold tracking-tight">Todo lo que necesitas para gestionar a tu equipo</h2>
            <p className="mt-4 text-slate-600 text-lg">Diseñado para ser potente para ti y a la vez muy simple para tus empleados.</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {FEATURES.map((f, i) => (
              <motion.div key={f.title} initial="hidden" whileInView="show" viewport={{ once: true }} custom={i % 3} variants={fadeUp}
                className={`bg-white rounded-3xl border border-slate-200/60 shadow-sm hover:shadow-md hover:-translate-y-1 transition-all p-8 ${f.span}`} data-testid={`feature-${i}`}>
                <div className="h-12 w-12 rounded-2xl bg-[#eaf2fb] flex items-center justify-center mb-4"><f.icon className="h-6 w-6 text-[#003366]" /></div>
                <h3 className="font-heading text-xl font-bold tracking-tight">{f.title}</h3>
                <p className="mt-2 text-slate-600 leading-relaxed">{f.desc}</p>
              </motion.div>
            ))}
            <motion.div initial="hidden" whileInView="show" viewport={{ once: true }} variants={fadeUp} className="bg-slate-900 text-white rounded-3xl p-8 flex flex-col justify-center">
              <Building2 className="h-7 w-7 text-[#2e6bb0] mb-3" />
              <h3 className="font-heading text-xl font-bold">Multi-empresa</h3>
              <p className="mt-2 text-slate-300">Gestiona varias empresas o centros desde un único panel. Marca blanca por cliente.</p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* KIOSK HIGHLIGHT */}
      <section className="py-24 bg-white border-y border-slate-200/60">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 grid md:grid-cols-2 gap-12 items-center">
          <div className="rounded-3xl overflow-hidden shadow-xl border border-slate-200/60 order-2 md:order-1">
            <img src={KIOSK_IMG} alt="Fichaje en obra con tablet" className="w-full h-full object-cover" />
          </div>
          <div className="order-1 md:order-2">
            <p className="text-xs font-bold tracking-[0.2em] uppercase text-[#003366] mb-3">Modo Quiosco</p>
            <h2 className="font-heading text-3xl md:text-4xl font-bold tracking-tight">Una tablet en la entrada. Fichaje en 2 segundos.</h2>
            <p className="mt-4 text-slate-600 text-lg leading-relaxed">Ideal para obras, naves y oficinas. Tus empleados fichan con su PIN personal o escaneando su código QR. Sin apps que instalar, sin complicaciones.</p>
            <ul className="mt-6 space-y-3">
              {[["PIN personal por empleado", QrCode], ["Escaneo de código QR con la cámara", IdCard], ["Funciona en cualquier tablet", Monitor], ["Registro a prueba de errores", ShieldCheck]].map(([t, Ic]) => (
                <li key={t} className="flex items-center gap-3 text-slate-700"><span className="h-7 w-7 rounded-full bg-[#eaf2fb] flex items-center justify-center"><Ic className="h-4 w-4 text-[#003366]" /></span>{t}</li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section className="py-24 md:py-32">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center max-w-2xl mx-auto mb-16">
            <p className="text-xs font-bold tracking-[0.2em] uppercase text-[#003366] mb-3">Cómo funciona</p>
            <h2 className="font-heading text-3xl md:text-4xl font-bold tracking-tight">En marcha en menos de 10 minutos</h2>
          </div>
          <div className="grid md:grid-cols-3 gap-12">
            {STEPS.map((s, i) => (
              <motion.div key={s.n} initial="hidden" whileInView="show" viewport={{ once: true }} custom={i} variants={fadeUp} className="text-center">
                <div className="font-heading text-5xl font-black text-[#cfe0f2] mb-2">{s.n}</div>
                <h3 className="font-heading text-xl font-bold tracking-tight">{s.title}</h3>
                <p className="mt-2 text-slate-600 leading-relaxed">{s.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* PRICING */}
      <section id="pricing" className="py-24 md:py-32 bg-white border-y border-slate-200/60">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center max-w-2xl mx-auto mb-16">
            <p className="text-xs font-bold tracking-[0.2em] uppercase text-[#003366] mb-3">Precios</p>
            <h2 className="font-heading text-3xl md:text-4xl font-bold tracking-tight">Planes simples, sin sorpresas</h2>
            <p className="mt-4 text-slate-600 text-lg">Precios orientativos. Empieza gratis con la demo y elige cuando lo tengas claro.</p>
            <div className="mt-7 inline-flex items-center gap-1 p-1 rounded-full bg-slate-100 border border-slate-200" data-testid="billing-toggle">
              <button onClick={() => setAnnual(false)} data-testid="billing-monthly"
                className={`px-5 py-2 rounded-full text-sm font-semibold transition-all ${!annual ? "bg-white shadow-sm text-slate-900" : "text-slate-500"}`}>Mensual</button>
              <button onClick={() => setAnnual(true)} data-testid="billing-annual"
                className={`px-5 py-2 rounded-full text-sm font-semibold transition-all flex items-center gap-2 ${annual ? "bg-white shadow-sm text-slate-900" : "text-slate-500"}`}>
                Anual <span className="text-xs font-bold text-emerald-600 bg-emerald-100 px-2 py-0.5 rounded-full">-25%</span>
              </button>
            </div>
            <div className="mt-4 inline-flex items-center gap-1.5 text-sm font-semibold text-[#FF8C00]" data-testid="launch-banner">
              <Sparkles className="h-4 w-4" /> Oferta de lanzamiento: primer año con descuento en el pago anual
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 items-start">
            {PLANS.map((p, i) => {
              const annualNormal = p.monthly ? Math.round(p.monthly * 12 * 0.75) : null;
              const firstYear = p.launchYear1 != null ? p.launchYear1 : (p.launchPct ? annualNormal * (1 - p.launchPct) : null);
              const hasLaunch = annual && firstYear != null;
              return (
              <motion.div key={p.name} initial="hidden" whileInView="show" viewport={{ once: true }} custom={i} variants={fadeUp}
                className={`relative bg-white rounded-3xl p-8 transition-all hover:-translate-y-1 ${p.popular ? "border-2 border-[#003366] shadow-xl shadow-[#003366]/10 md:scale-105" : "border border-slate-200/60 shadow-sm"}`} data-testid={`plan-${p.name.toLowerCase()}`}>
                {p.popular && <span className="absolute -top-3 left-1/2 -translate-x-1/2 px-4 py-1 rounded-full bg-[#FF8C00] text-white text-xs font-bold tracking-wide">MÁS POPULAR</span>}
                <h3 className="font-heading text-xl font-bold">{p.name}</h3>
                <p className="text-slate-500 text-sm mt-1">{p.desc}</p>
                {p.monthly ? (
                  <div className="mt-5 min-h-[112px]" data-testid={`price-${p.name.toLowerCase()}`}>
                    {hasLaunch ? (
                      <>
                        <div className="flex items-end gap-1.5">
                          <span className="font-heading text-4xl font-extrabold tracking-tight">{fmtEur(firstYear)}</span>
                          <span className="text-slate-400 mb-1">primer año</span>
                        </div>
                        <div className="text-sm text-slate-400 mt-1">
                          <span className="line-through">{annualNormal}€/año</span> · luego {annualNormal}€/año
                        </div>
                        <div className="mt-2 inline-flex items-center gap-1 text-xs font-bold text-[#FF8C00] bg-[#fff3e6] border border-[#ffd9b0] px-2.5 py-1 rounded-full">
                          <Sparkles className="h-3 w-3" /> Oferta de lanzamiento{p.launchPct ? " · −50%" : ""}
                        </div>
                      </>
                    ) : (
                      <>
                        <div className="flex items-end gap-1">
                          <span className="font-heading text-4xl font-extrabold tracking-tight">{p.monthly}€</span>
                          <span className="text-slate-400 mb-1">/mes</span>
                        </div>
                        <div className="text-sm text-slate-400 mt-1">facturación mensual</div>
                        <div className="mt-2 text-xs font-semibold text-[#003366]">Oferta de lanzamiento en el pago anual →</div>
                      </>
                    )}
                  </div>
                ) : (
                  <div className="mt-5 min-h-[112px]" data-testid={`price-${p.name.toLowerCase()}`}>
                    <div className="flex items-end gap-1"><span className="font-heading text-4xl font-extrabold tracking-tight">A medida</span></div>
                    <div className="text-sm text-slate-400 mt-1">{annual ? "Descuento del 25% por pago anual" : "Según nº de empleados y módulos"}</div>
                  </div>
                )}
                <Button onClick={() => scrollContact(p.name)} data-testid={`plan-cta-${p.name.toLowerCase()}`}
                  className={`w-full mt-6 rounded-full font-semibold transition-all hover:-translate-y-0.5 ${p.popular ? "bg-[#003366] hover:bg-[#04284a] text-white shadow-lg shadow-[#003366]/25" : "bg-slate-900 hover:bg-slate-800 text-white"}`}>
                  {p.name === "Empresa" ? "Hablar con ventas" : "Empezar"}
                </Button>
                <ul className="mt-7 space-y-3">
                  {p.features.map((f) => <li key={f} className="flex items-start gap-3 text-sm text-slate-600"><Check className="h-5 w-5 text-[#003366] shrink-0" />{f}</li>)}
                </ul>
                {p.note && (
                  <div className="mt-5 flex items-start gap-2 rounded-xl bg-[#eaf2fb] border border-[#cfe0f2] p-3 text-xs text-[#003366]" data-testid={`plan-note-${p.name.toLowerCase()}`}>
                    <Sparkles className="h-4 w-4 shrink-0 mt-0.5" />
                    <span>{p.note}</span>
                  </div>
                )}
              </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* TESTIMONIALS */}
      <section className="py-24 md:py-32">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center max-w-2xl mx-auto mb-14">
            <p className="text-xs font-bold tracking-[0.2em] uppercase text-[#003366] mb-3">Testimonios</p>
            <h2 className="font-heading text-3xl md:text-4xl font-bold tracking-tight">Empresarios que recuperaron su tiempo</h2>
          </div>
          <div className="grid md:grid-cols-2 gap-6">
            {[
              { img: T1, name: "Laura Giménez", role: "Gerente · Construcciones del Norte", text: "Antes perdía horas cuadrando partes. Con el modo quiosco en obra, el fichaje es automático y los informes salen solos." },
              { img: T2, name: "Marc Soler", role: "Director · Logística Vega", text: "La gestión de vacaciones era un caos de emails. Ahora todo está en un sitio y mi equipo lo adora." },
            ].map((t) => (
              <motion.div key={t.name} initial="hidden" whileInView="show" viewport={{ once: true }} variants={fadeUp} className="bg-white rounded-3xl border border-slate-200/60 shadow-sm p-8">
                <div className="flex gap-1 mb-4">{[...Array(5)].map((_, i) => <Star key={i} className="h-4 w-4 fill-amber-400 text-amber-400" />)}</div>
                <p className="text-lg text-slate-700 leading-relaxed">“{t.text}”</p>
                <div className="mt-6 flex items-center gap-3">
                  <img src={t.img} alt={t.name} className="h-12 w-12 rounded-full object-cover" />
                  <div><div className="font-semibold">{t.name}</div><div className="text-sm text-slate-500">{t.role}</div></div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CONTACT */}
      <section id="contact" ref={contactRef} className="py-24 md:py-32 bg-white border-t border-slate-200/60">
        <div className="max-w-6xl mx-auto px-6 lg:px-8 grid md:grid-cols-2 gap-12 items-center">
          <div>
            <p className="text-xs font-bold tracking-[0.2em] uppercase text-[#003366] mb-3">Contacto</p>
            <h2 className="font-heading text-3xl md:text-4xl font-bold tracking-tight">Hablemos de tu empresa</h2>
            <p className="mt-4 text-slate-600 text-lg leading-relaxed">Cuéntanos qué necesitas y te preparamos una demo personalizada con tu marca. Sin compromiso.</p>
            <div className="mt-8 space-y-4">
              <a href={`mailto:${CONTACT_EMAIL}`} data-testid="contact-email-link" className="flex items-center gap-3 text-slate-700 hover:text-[#04284a] transition-colors"><span className="h-10 w-10 rounded-xl bg-[#eaf2fb] flex items-center justify-center"><Mail className="h-5 w-5 text-[#003366]" /></span>{CONTACT_EMAIL}</a>
              <div className="flex items-center gap-3 text-slate-700"><span className="h-10 w-10 rounded-xl bg-[#eaf2fb] flex items-center justify-center"><MapPin className="h-5 w-5 text-[#003366]" /></span>España · 100% online</div>
              <div className="flex items-center gap-3 text-slate-700"><span className="h-10 w-10 rounded-xl bg-[#eaf2fb] flex items-center justify-center"><ShieldCheck className="h-5 w-5 text-[#003366]" /></span>Cumplimiento del registro de jornada</div>
              <div className="flex items-center gap-3 text-slate-700"><span className="h-10 w-10 rounded-xl bg-[#eaf2fb] flex items-center justify-center"><Sparkles className="h-5 w-5 text-[#003366]" /></span>Dominio propio <span className="font-semibold">(plan Empresa)</span>: tuempresa.fichando.app</div>
            </div>
          </div>
          <ContactForm />
        </div>
      </section>

      {/* FOOTER */}
      <footer className="bg-slate-900 text-slate-300">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 py-16">
          <div className="grid md:grid-cols-4 gap-10">
            <div className="md:col-span-2">
              <div className="mb-4"><Logo size={38} dark textSize="text-xl" /></div>
              <p className="max-w-sm text-slate-400">El control horario sencillo para empresas. Fichaje, vacaciones, informes y modo quiosco. Marca blanca.</p>
              <p className="mt-4 text-sm text-slate-500">www.fichando.app</p>
              <a href={`mailto:${CONTACT_EMAIL}`} data-testid="footer-email" className="mt-1 inline-flex items-center gap-2 text-sm text-slate-400 hover:text-white transition-colors"><Mail className="h-4 w-4" /> {CONTACT_EMAIL}</a>
            </div>
            <div>
              <div className="font-semibold text-white mb-3">Producto</div>
              <ul className="space-y-2 text-sm">
                <li><a href="#features" className="hover:text-white">Características</a></li>
                <li><a href="#pricing" className="hover:text-white">Precios</a></li>
                <li><Link to="/login" className="hover:text-white">Probar demo</Link></li>
              </ul>
            </div>
            <div>
              <div className="font-semibold text-white mb-3">Empresa</div>
              <ul className="space-y-2 text-sm">
                <li><a href="#contact" className="hover:text-white">Contacto</a></li>
                <li><a href={`mailto:${CONTACT_EMAIL}`} className="hover:text-white">{CONTACT_EMAIL}</a></li>
                <li><a href="#" className="hover:text-white">Privacidad</a></li>
                <li><a href="#" className="hover:text-white">Términos</a></li>
              </ul>
            </div>
          </div>
          <div className="mt-12 pt-8 border-t border-slate-800 text-sm text-slate-500 flex flex-col sm:flex-row justify-between gap-2">
            <span>© 2026 Fichando. Todos los derechos reservados.</span>
            <span>Hecho con cariño para empresarios.</span>
          </div>
        </div>
      </footer>
    </div>
  );
}
